import { Camera, ChevronRight, PencilLine, Settings } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import type { ExpenseDTO, Overview } from "@/lib/expenses";
import { BottomNav } from "@/components/bottom-nav";
import { SideNav } from "@/components/side-nav";
import { MonthSwitcher } from "@/components/month-switcher";
import { SummaryHeader } from "@/components/summary-header";
import { CategoryBreakdown } from "@/components/category-breakdown";
import { ScopeSplit } from "@/components/scope-split";
import { ScopeFilter } from "@/components/scope-filter";
import { FuelCost } from "@/components/fuel-cost";
import { DailyChart } from "@/components/daily-chart";
import { ExportMenu } from "@/components/export-menu";
import { ExpenseCard } from "@/components/expense-card";
import { SyncStatus } from "@/components/sync-status";
import { LogoutButton } from "@/components/logout-button";
import { PageError, PageLoading } from "@/components/page-state";
import { formatMoney } from "@/lib/format";
import { query } from "@/lib/api";
import { useApi } from "@/lib/use-api";

type DayGroup = { date: string; items: ExpenseDTO[]; totalCents: number };

// Bucket the (already date-desc) expenses into per-day groups, preserving order.
function groupByDay(expenses: ExpenseDTO[]): DayGroup[] {
  const groups: DayGroup[] = [];
  const index = new Map<string, DayGroup>();
  for (const e of expenses) {
    let g = index.get(e.date);
    if (!g) {
      g = { date: e.date, items: [], totalCents: 0 };
      index.set(e.date, g);
      groups.push(g);
    }
    g.items.push(e);
    g.totalCents += e.totalCents;
  }
  return groups;
}

function dayLabel(date: string): string {
  const d = parseISO(date);
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "EEE, MMM d");
}

export default function DashboardPage() {
  const [searchParams] = useSearchParams();
  const month = searchParams.get("month") ?? new Date().toISOString().slice(0, 7);
  const category = searchParams.get("category") ?? undefined;
  const scope = searchParams.get("scope") ?? undefined;

  // One request for what used to be five parallel server-side loaders.
  const { data, error, loading, reload } = useApi<Overview>(
    `/api/overview${query({ month, category, scope })}`,
  );

  if (error) return <PageError message={error} onRetry={reload} />;
  if (loading || !data) return <PageLoading />;

  const { summary, insights, expenses, daily, fuel } = data;

  // already sorted by date desc upstream, but ensure newest first
  const sorted = [...expenses].sort((a, b) =>
    a.date < b.date ? 1 : a.date > b.date ? -1 : 0,
  );

  // Today's running total — only when viewing the current month.
  const todayISO = new Date().toISOString().slice(0, 10);
  const todayCents = todayISO.startsWith(month)
    ? (daily.days.find((d) => d.date === todayISO)?.totalCents ?? 0)
    : 0;

  // Home shows today's expenses plus a short tail of recent ones; the full
  // history lives on the dedicated /transactions page.
  const HOME_MIN = 6;
  const todayCount = sorted.filter((e) => e.date === todayISO).length;
  const shown = sorted.slice(0, Math.max(HOME_MIN, todayCount));
  const hasMore = sorted.length > shown.length;
  const groups = groupByDay(shown);

  return (
    <>
      <SideNav />
      <div className="min-h-dvh lg:pl-64">
        <div className="mx-auto max-w-md px-4 pb-32 pt-[calc(env(safe-area-inset-top)+1.25rem)] lg:max-w-6xl lg:px-10 lg:pb-14 lg:pt-9">
          <header className="mb-5 flex flex-wrap items-center justify-between gap-3 lg:mb-8">
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Overview
              </p>
              <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">
                Spending
              </h1>
            </div>
            <div className="flex w-full items-center gap-2 lg:w-auto">
              <div className="flex-1 lg:w-60">
                <MonthSwitcher month={month} />
              </div>
              <SyncStatus />
              <ExportMenu month={month} scope={scope} />
              {/* Settings and log out only ride the header on mobile; the
                  desktop side nav already carries both. */}
              <span className="flex items-center gap-1 lg:hidden">
                <Link
                  to="/settings"
                  aria-label="Settings"
                  className="flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-muted"
                >
                  <Settings className="size-4" />
                </Link>
                <LogoutButton />
              </span>
            </div>
          </header>

          <div className="mb-4">
            <ScopeFilter month={month} category={category} active={scope} />
          </div>

          {summary.count === 0 ? (
            <div className="mt-12 flex flex-col items-center gap-4 text-center lg:mt-24">
              <div className="flex size-16 items-center justify-center rounded-3xl bg-card text-3xl shadow-sm ring-1 ring-black/[0.05]">
                🧾
              </div>
              <div>
                <p className="font-semibold">No expenses yet</p>
                <p className="text-sm text-muted-foreground">
                  Add one manually or scan a receipt.
                </p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Link
                  to="/add"
                  className="inline-flex h-12 items-center gap-2 rounded-full bg-primary px-6 font-semibold text-primary-foreground transition-transform active:scale-[0.98]"
                >
                  <PencilLine className="size-5" />
                  Add your first expense
                </Link>
                <Link
                  to="/capture"
                  className="inline-flex h-11 items-center gap-2 rounded-full px-4 text-sm font-medium text-muted-foreground transition-colors active:bg-muted"
                >
                  <Camera className="size-4" />
                  Or scan a receipt
                </Link>
              </div>
            </div>
          ) : (
            <div className="lg:grid lg:grid-cols-3 lg:items-start lg:gap-5">
              {/* Hero — main column, top */}
              <div className="card-lift animate-rise lg:col-span-2 lg:col-start-1 lg:row-start-1">
                <SummaryHeader
                  insights={insights}
                  busiestDay={daily.busiestDay}
                  todayCents={todayCents}
                />
              </div>

              {/* Category breakdown — sticky right rail */}
              <div
                className="card-lift animate-rise mt-3 lg:sticky lg:top-8 lg:col-start-3 lg:row-span-3 lg:row-start-1 lg:mt-0 lg:self-start"
                style={{ animationDelay: "80ms" }}
              >
                <CategoryBreakdown
                  summary={summary}
                  month={month}
                  active={category}
                  scope={scope}
                />
              </div>

              {/* Scope split + fuel cost — right rail, under the breakdown */}
              <div
                className="card-lift animate-rise mt-3 space-y-3 lg:col-start-3 lg:row-start-4 lg:mt-3"
                style={{ animationDelay: "110ms" }}
              >
                <ScopeSplit
                  summary={summary}
                  month={month}
                  active={scope}
                  category={category}
                />
                <FuelCost fuel={fuel} />
              </div>

              {/* Recent expenses — main column */}
              <section
                className="animate-rise mt-6 lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:mt-5"
                style={{ animationDelay: "140ms" }}
              >
                <div className="mb-3 flex items-center justify-between px-1">
                  <h2 className="text-sm font-semibold">
                    {category ? `${category} expenses` : "Recent expenses"}
                  </h2>
                  {category ? (
                    <Link
                      to={`/?month=${month}`}
                      className="text-xs font-medium text-muted-foreground underline"
                    >
                      Show all
                    </Link>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {sorted.length} total
                    </span>
                  )}
                </div>

                {groups.length === 0 ? (
                  <p className="rounded-[1.75rem] bg-card px-4 py-8 text-center text-sm text-muted-foreground shadow-sm ring-1 ring-black/[0.05]">
                    No expenses in this category.
                  </p>
                ) : (
                  <div className="space-y-5">
                    {groups.map((g) => (
                      <div key={g.date}>
                        <div className="mb-2 flex items-baseline justify-between px-1.5">
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {dayLabel(g.date)}
                          </h3>
                          <span className="text-xs font-medium tabular-nums text-muted-foreground">
                            {formatMoney(g.totalCents, summary.currency)}
                          </span>
                        </div>
                        <div className="divide-y divide-border/70 overflow-hidden rounded-[1.75rem] bg-card shadow-sm ring-1 ring-black/[0.05]">
                          {g.items.map((e) => (
                            <ExpenseCard key={e.id} expense={e} />
                          ))}
                        </div>
                      </div>
                    ))}

                    {hasMore && (
                      <Link
                        to="/transactions"
                        className="flex h-12 items-center justify-center gap-1 rounded-2xl bg-card text-sm font-medium shadow-sm ring-1 ring-black/[0.05] transition-colors hover:bg-muted active:bg-muted"
                      >
                        See all transactions
                        <ChevronRight className="size-4" />
                      </Link>
                    )}
                  </div>
                )}
              </section>

              {/* Daily spend — main column, below recent */}
              {!category && (
                <div
                  className="card-lift animate-rise mt-6 lg:col-span-2 lg:col-start-1 lg:row-start-3 lg:mt-5"
                  style={{ animationDelay: "200ms" }}
                >
                  <DailyChart data={daily} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </>
  );
}
